import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Metric, Modal, TextInput, TileGroup } from "@opendate-app/setlist";
import client from "../../../shared/api/client";
import { toastError, toastSuccess } from "../../../shared/toastHelper";
import { moneyLabel } from "./scheduleFormatters";

const FORMATS = [
  { value: "gusto", label: "Gusto", supportingText: "One row per person per position with regular, overtime and double overtime hours. Gusto pays them at its own rates." },
  { value: "adp_run", label: "ADP RUN", supportingText: "ADP's Time Sheet Import file: one row per person per earnings code." },
  { value: "detail", label: "Detail", supportingText: "One row per time card, with rate and gross pay." },
  { value: "volunteer", label: "Volunteer hours", supportingText: "One row per volunteer time card. Doesn't mark the period exported." },
];
const ADP_FIELDS = [
  { key: "company_code", label: "Company code", hint: "Your ADP IID" },
  { key: "regular_code", label: "Regular earnings code" },
  { key: "overtime_code", label: "Overtime earnings code" },
  { key: "double_time_code", label: "Double time earnings code" },
];
const ADP_DEFAULTS = { company_code: "", regular_code: "REG", overtime_code: "OT", double_time_code: "DT" };
const ADP_STORAGE_KEY = "staffing.payrollExport.adp";

const storedAdp = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(ADP_STORAGE_KEY) || "{}") || {};
    return Object.fromEntries(Object.entries(ADP_DEFAULTS).map(([key, fallback]) => [key, typeof stored[key] === "string" ? stored[key] : fallback]));
  } catch {
    return ADP_DEFAULTS;
  }
};

const storeAdp = (adp) => {
  try {
    window.localStorage.setItem(ADP_STORAGE_KEY, JSON.stringify(adp));
  } catch {
    return null;
  }
};

const filenameFrom = (headers) => /filename="([^"]+)"/.exec(headers?.["content-disposition"] || "")?.[1] || "payroll.csv";

const download = (data, filename) => {
  const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const blobError = async (error) => {
  try {
    return JSON.parse(await error.response.data.text()).errors[0];
  } catch {
    return null;
  }
};

const PayrollExportModal = ({ path, onClose, onExported }) => {
  const [format, setFormat] = useState("gusto");
  const [adp, setAdp] = useState(storedAdp);
  const [summary, setSummary] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    client
      .get(path)
      .then(({ data }) => setSummary(data))
      .catch(() => toastError("Could not load this pay period's totals."));
  }, [path]);

  const adpMissing = format === "adp_run" && Object.values(adp).some((value) => !value.trim());

  const submit = async () => {
    setSaving(true);
    try {
      const response = await client.post(path, { export_format: format, ...(format === "adp_run" ? { adp } : {}) }, { responseType: "blob" });
      if (format === "adp_run") storeAdp(adp);
      download(response.data, filenameFrom(response.headers));
      toastSuccess(format === "volunteer" ? "Volunteer hours downloaded." : "Payroll downloaded. This pay period is now marked exported.");
      onExported();
    } catch (error) {
      toastError((await blobError(error)) || "Could not export this pay period.");
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      width={640}
      showClose={false}
      onClose={onClose}
      title="Export payroll"
      description="Download this pay period's approved hours for your payroll provider."
      footer={
        <div className="d-flex justify-content-end" style={{ gap: 12, width: "100%" }}>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={saving || adpMissing} onClick={submit}>
            Download CSV
          </Button>
        </div>
      }
    >
      <TileGroup name="payroll-export-format" type="radio" columns={2} value={format} onChange={setFormat} items={FORMATS} />

      {format === "adp_run" && (
        <div className="mt-3" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
          {ADP_FIELDS.map((field) => (
            <TextInput
              key={field.key}
              id={`payroll-export-${field.key}`}
              label={field.label}
              hint={field.hint}
              value={adp[field.key]}
              onChange={(event) => setAdp({ ...adp, [field.key]: event.target.value })}
            />
          ))}
        </div>
      )}

      {summary && (
        <>
          <div className="mt-3" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12 }}>
            {format === "volunteer" ? (
              <>
                <Metric label="Volunteers" value={String(summary.volunteers)} />
                <Metric label="Volunteer hours" value={summary.volunteerHours.toFixed(2)} />
              </>
            ) : (
              <>
                <Metric label="Regular hours" value={summary.regularHours.toFixed(2)} />
                <Metric label="Overtime hours" value={summary.overtimeHours.toFixed(2)} />
                <Metric label="Double time hours" value={summary.doubleTimeHours.toFixed(2)} />
                <Metric label="Gross pay" value={moneyLabel(summary.grossPay)} change={{ label: `${summary.people} ${summary.people === 1 ? "person" : "people"}` }} />
              </>
            )}
          </div>
          {format !== "volunteer" && summary.warnings.length > 0 && (
            <div className="mt-3">
              <Alert color="warning" description={summary.warnings.join(" ")} />
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

PayrollExportModal.propTypes = {
  path: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onExported: PropTypes.func.isRequired,
};

export default PayrollExportModal;
